// New component to show po_nos as a table
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  CircularProgress,
  TableRow,
  TableCell,
  IconButton,
  Box,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Card,
  TableContainer,
  makeStyles,
  colors,
  Tooltip
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { Autocomplete } from '@material-ui/lab';
import { PurchaseOrderItem } from 'src/redux/slices/cheque-management';
import { CustomKeyboardEvent } from 'src/types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { AlternatingColorTableRow, LinkComponent } from 'src/components';
import clsx from 'clsx';
import { copyToClipboard, formatCurrency } from 'src/utils';
import FileCopyIcon from '@material-ui/icons/FileCopy';

const useStyles = makeStyles(() => ({
  root: {},
  imgPreview: {
    width: undefined,
    height: 50
  },
  priceInput: {
    height: 30
  },
  lightGrey: {
    backgroundColor: colors.blueGrey[50]
  },
  table: {
    border: '1px solid #a4abba'
  },
  tableCell: {
    textAlign: 'start',
    borderBottom: '1px solid #a4abba'
  },
  tableRow: {
    borderBottom: '1px solid #a4abba'
  }
}));

type PoNosTableProps = {
  isPoInputLoading?: boolean;
  grandTotal?: number;
  poNos: PurchaseOrderItem[];
  onRemove: (index: number) => void;
  onAddNewPoNos: (input: string) => void;
  supplierId?: number;
  setAddPoInput?: (val: string) => void;
  addPoInput?: string;
};

const component = ({
  poNos,
  addPoInput,
  grandTotal = 0,
  isPoInputLoading = false,
  onRemove,
  onAddNewPoNos,
  setAddPoInput,
  supplierId
}: PoNosTableProps) => {
  const classes = useStyles();
  const [filteredPoNos, setFilteredPoNos] = useState<PurchaseOrderItem[]>(
    poNos
  );

  const onKeyDown = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onAddNewPoNos(addPoInput || '');
    }
  };

  const onHandleCopyProductName = (productName?: string) => {
    copyToClipboard(productName || '');
  };

  useEffect(() => {
    setFilteredPoNos(poNos);
  }, [poNos]);

  return (
    <Box mt={2}>
      <Grid
        container
        style={{ justifyContent: 'space-between', rowGap: '1em' }}
      >
        <Grid item xs={12} sm={2}>
          <Autocomplete
            freeSolo
            fullWidth
            options={[]}
            inputValue={addPoInput}
            value={null}
            disabled={!supplierId || isPoInputLoading}
            onInputChange={(event, newInputValue) => {
              setAddPoInput?.(newInputValue);
            }}
            onKeyDown={onKeyDown}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                variant="outlined"
                label="Add PO No."
                helperText={!supplierId ? 'Choose supplier first' : ''}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isPoInputLoading && (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Grid>
        {poNos?.[0] ? (
          <Grid item xs={12} sm={2}>
            <Autocomplete
              freeSolo
              fullWidth
              options={poNos}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.po_no ?? ''
              }
              onInputChange={(_, newInputValue) => {
                if (newInputValue) {
                  setFilteredPoNos(
                    poNos?.filter((poNo) => poNo.po_no == newInputValue) || []
                  );
                } else {
                  setFilteredPoNos(poNos);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label="Search PO No."
                />
              )}
            />
          </Grid>
        ) : null}
      </Grid>
      <Card
        className={clsx(classes.root)}
        style={{ borderRadius: 0, marginTop: 10 }}
      >
        <PerfectScrollbar>
          <TableContainer style={{ maxHeight: 550 }}>
            <Table className={classes.table} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCell}>PO No</TableCell>
                  <TableCell className={classes.tableCell}>DR No</TableCell>
                  <TableCell className={classes.tableCell}>Terms</TableCell>
                  <TableCell className={classes.tableCell}>Amount</TableCell>
                  <TableCell className={classes.tableCell} align="right">
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPoNos && filteredPoNos.length > 0 ? (
                  filteredPoNos.map((item: PurchaseOrderItem, index) => (
                    <AlternatingColorTableRow key={item?.po_no ?? index}>
                      <TableCell className={classes.tableCell}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center'
                          }}
                        >
                          <Tooltip title="Copy">
                            <FileCopyIcon
                              style={{ cursor: 'pointer', marginRight: 10 }}
                              onClick={() =>
                                onHandleCopyProductName(item?.po_no)
                              }
                            />
                          </Tooltip>
                          <LinkComponent
                            href={`/app/purchase-order/${item?.po_id}`}
                            title={`${item?.po_no}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        </Box>
                      </TableCell>

                      <TableCell className={classes.tableCell}>
                        <Typography>{item?.dr_no || '--'}</Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography>{item?.terms || '--'}</Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Typography>
                          {formatCurrency(item?.amount || item.total_amount) ||
                            '--'}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell} align="right">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => onRemove(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </AlternatingColorTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      style={{ textAlign: 'center', padding: '1em' }}
                    >
                      <Typography color="textSecondary">
                        -- No purchase orders added --
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PerfectScrollbar>
      </Card>
      <Box style={{ display: 'flex', justifyContent: 'end', marginTop: 15 }}>
        <Typography variant="h4">
          {`Grand Total: ${formatCurrency(grandTotal)}`}
        </Typography>
      </Box>
    </Box>
  );
};

export const PONosTable = React.memo(component);
